import React, { useEffect, useState } from "react";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import axios from "axios";
import Select from "react-select";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTimes,
  faUser,
  faMobileAlt,
  faMapMarkerAlt,
  faSeedling,
  faTint,
  faFlask,
  faSave,
  faUserPlus,
  faPen,
  faBox,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import CreatableSelect from "react-select/creatable";
import getFarmerDetails from "../FarmerDetails/getFarmerDetails";
import editFarmerDetails from "../FarmerDetails/editFarmerDetails";
import deleteFarmerApi from "./deleteFarmer";
import getCrops from "../IssueDetails/getAllCrops";
import getProducts from "../IssueDetails/getProducts";
import ModalAlert from "../reusable/ModalAlert";
import ConfirmBox from "../reusable/ConfirmBox";
import stateDistrictTalukaData from "../../assets/static/IndiaStateDistrictTaluka";
import getNextRegNo from "./getNextRegNo";

// ─── Palette (mirrors FarmerDrawer) ──────────────────────────────────────────
const C = {
  dark: "#1a3328",
  mid: "#2d5a3d",
  bright: "#3d8c5e",
  light: "#e8f5ee",
  page: "#f4f6f5",
  border: "#d8e4dc",
  white: "#ffffff",
  textDk: "#1a2e22",
  textGr: "#6c757d",
  error: "#c62828",
  gold: "#b8860b",
};

// ─── Select theme ─────────────────────────────────────────────────────────────
const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 38,
    borderRadius: 8,
    fontSize: "0.88rem",
    borderColor: state.isFocused ? C.bright : C.border,
    boxShadow: state.isFocused ? `0 0 0 2px rgba(61,140,94,0.15)` : "none",
    "&:hover": { borderColor: C.bright },
    backgroundColor: C.white,
  }),
  option: (base, state) => ({
    ...base,
    fontSize: "0.85rem",
    backgroundColor: state.isSelected
      ? C.mid
      : state.isFocused
        ? C.light
        : "#fff",
    color: state.isSelected ? "#fff" : C.textDk,
  }),
  placeholder: (base) => ({ ...base, color: "#aab8b0", fontSize: "0.85rem" }),
  singleValue: (base) => ({ ...base, color: C.textDk, fontSize: "0.88rem" }),
  multiValue: (base) => ({
    ...base,
    backgroundColor: C.light,
    borderRadius: 6,
  }),
  multiValueLabel: (base) => ({
    ...base,
    color: C.mid,
    fontSize: "0.8rem",
    fontWeight: 600,
  }),
  multiValueRemove: (base) => ({
    ...base,
    color: C.mid,
    "&:hover": { backgroundColor: C.mid, color: "#fff" },
  }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

// ─── Shared input style ───────────────────────────────────────────────────────
const input = {
  width: "100%",
  height: 38,
  borderRadius: 8,
  border: `1.5px solid ${C.border}`,
  padding: "0 0.8rem",
  fontSize: "0.88rem",
  color: C.textDk,
  backgroundColor: C.white,
  outline: "none",
  boxSizing: "border-box",
};

const inputDisabled = {
  ...input,
  backgroundColor: "#f5f5f5",
  color: C.textGr,
  cursor: "not-allowed",
};

// ─── Field wrapper ────────────────────────────────────────────────────────────
const FF = ({ label, required, error, children }) => (
  <div style={{ marginBottom: "0.85rem" }}>
    <label
      style={{
        display: "block",
        fontSize: "0.68rem",
        fontWeight: 700,
        letterSpacing: "0.07em",
        color: C.textGr,
        textTransform: "uppercase",
        marginBottom: 4,
      }}
    >
      {label}
      {required && <span style={{ color: C.error, marginLeft: 2 }}>*</span>}
    </label>
    {children}
    {error && (
      <div style={{ color: C.error, fontSize: "0.72rem", marginTop: 3 }}>
        {error}
      </div>
    )}
  </div>
);

// ─── Section card ─────────────────────────────────────────────────────────────
const Section = ({ title, icon, children }) => (
  <div
    style={{
      backgroundColor: C.white,
      borderRadius: 10,
      border: `1px solid ${C.border}`,
      marginBottom: "1rem",
      boxShadow: "0 1px 6px rgba(20,50,30,0.04)",
      overflow: "hidden",
    }}
  >
    <div
      style={{
        padding: "0.6rem 1rem",
        backgroundColor: "#f1f7f3",
        borderBottom: `1px solid ${C.border}`,
        display: "flex",
        alignItems: "center",
        gap: 7,
        fontSize: "0.72rem",
        fontWeight: 700,
        color: C.mid,
        textTransform: "uppercase",
        letterSpacing: "0.08em",
      }}
    >
      {icon &&
        (typeof icon === "string" ? (
          <span style={{ fontSize: "0.85rem" }}>{icon}</span>
        ) : (
          <FontAwesomeIcon icon={icon} style={{ fontSize: "0.68rem" }} />
        ))}
      {title}
    </div>
    <div style={{ padding: "0.9rem 1rem" }}>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: "0 1.25rem",
        }}
      >
        {children}
      </div>
    </div>
  </div>
);

// helper: make full-width grid span
const Full = ({ children }) => (
  <div style={{ gridColumn: "1 / -1" }}>{children}</div>
);

// ─── Crop normalization ───────────────────────────────────────────────────────
const parseCropArr = (val) => {
  if (!val) return [];
  if (Array.isArray(val)) return val.map(String).filter(Boolean);
  if (typeof val === "string")
    return val
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
  return [];
};

// ─── Validation ───────────────────────────────────────────────────────────────
const schema = Yup.object({
  fullName: Yup.string()
    .matches(/^[a-zA-Z ]*$/, "Letters only")
    .required("Required"),
  mobileNumber: Yup.string()
    .matches(/^\d{10}$/, "Must be 10 digits")
    .required("Required"),
  whatsappNumber: Yup.string()
    .matches(/^\d{10}$/, "Must be 10 digits")
    .nullable(),
  landAcreage: Yup.string()
    .matches(/^[a-zA-Z0-9,. ]*$/, "Invalid characters")
    .nullable(),
  pinCode: Yup.string()
    .matches(/^\d{6}$/, "Must be 6 digits")
    .nullable(),
});

// ─── EMPTY initial values ─────────────────────────────────────────────────────
const EMPTY = {
  regNo: "",
  fullName: "",
  mobileNumber: "",
  whatsappNumber: "",
  townVillage: "",
  taluka: "",
  landAcreage: "",
  previousCropsCultivated: "",
  currentCropsCultivated: "",
  irrigSource: "",
  irrigFacilities: "",
  landType: "",
  soilTestingStatus: "Not Done",
  soilTestingLabName: "",
  lastSoilTestOn: "",
  productsUsed: "",
  existingCustomer: "No",
  usingProductsSince: "",
  paid: "No",
  phonecallio: "",
  pinCode: "",
  heardAbout: "",
  dealerPreference: "",
};

// ─── Header button style ──────────────────────────────────────────────────────
const hdrBtn = (bg, color) => ({
  height: 30,
  padding: "0 12px",
  borderRadius: 7,
  border: "none",
  backgroundColor: bg,
  color,
  fontSize: "0.78rem",
  fontWeight: 600,
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  gap: 5,
});

// ─── Main component ───────────────────────────────────────────────────────────
const FarmerFormDrawer = ({ mode, farmer, onClose, onSuccess, onDeleteSuccess }) => {
  const isEdit = mode === "edit";

  const [initialValues, setInitialValues] = useState(EMPTY);
  const [loadingData, setLoadingData] = useState(isEdit);
  const [allCrops, setAllCrops] = useState([]);
  const [selectedState, setSelectedState] = useState(null);
  const [selectedDistrict, setSelectedDistrict] = useState(null);
  const [currentCrops, setCurrentCrops] = useState([]);
  const [previousCrops, setPreviousCrops] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [selectedTaluka, setSelectedTaluka] = useState(null);
  const [alert, setAlert] = useState(null); // { variant, message }
  const [regNoLoading, setRegNoLoading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Load crops + products list
  useEffect(() => {
    getCrops().then((res) => {
      if (res?.status === 200) setAllCrops(res.data.map((i) => i.crop));
    });
    getProducts().then((res) => {
      const raw = Array.isArray(res?.data)
        ? res.data
        : Array.isArray(res?.data?.data)
          ? res.data.data
          : Array.isArray(res?.data?.products)
            ? res.data.products
            : [];
      setAllProducts(
        raw
          .map((p) => p.product || p.productName || p.name || "")
          .filter(Boolean),
      );
    });
  }, []);

  // Load farmer data for edit mode
  useEffect(() => {
    if (!isEdit) return;
    getFarmerDetails(farmer.mobileNumber).then((res) => {
      if (res?.status === 200) {
        const d = res.data;
        setInitialValues(d);
        setSelectedState(d.state ? { value: d.state, label: d.state } : null);
        setSelectedDistrict(
          d.district ? { value: d.district, label: d.district } : null,
        );
        setSelectedTaluka(
          d.taluka ? { value: d.taluka, label: d.taluka } : null,
        );
        setCurrentCrops(parseCropArr(d.currentCropsCultivated));
        setPreviousCrops(parseCropArr(d.previousCropsCultivated));
        setSelectedProducts(parseCropArr(d.productsUsed));
      }
      setLoadingData(false);
    });
  }, [isEdit, farmer]);

  const stateOptions = Object.keys(stateDistrictTalukaData).map((s) => ({
    value: s,
    label: s,
  }));
  const districtOptions = selectedState
    ? Object.keys(stateDistrictTalukaData[selectedState.value] || {}).map(
        (d) => ({ value: d, label: d }),
      )
    : [];

  const talukaOptions =
    selectedState && selectedDistrict
      ? (
          stateDistrictTalukaData[selectedState.value]?.[
            selectedDistrict.value
          ] || []
        ).map((t) => ({ value: t, label: t }))
      : [];

  const handleSubmit = (values, { setSubmitting }) => {
    if (!selectedState?.value || !selectedDistrict?.value) {
      setAlert({
        variant: "danger",
        message: "Please select State and District.",
      });
      setSubmitting(false);
      return;
    }

    const payload = {
      ...values,
      state: selectedState.value,
      district: selectedDistrict.value,
      taluka: selectedTaluka?.value || "",
      currentCropsCultivated: currentCrops.join(","),
      previousCropsCultivated: previousCrops.join(","),
      productsUsed: selectedProducts.join(","),
    };

    if (isEdit) {
      editFarmerDetails(payload).then((res) => {
        if (res?.status === 200) {
          onSuccess && onSuccess(payload);
          onClose();
        } else {
          const msg = res?.message || "";
          if (msg.includes("422")) {
            setAlert({
              variant: "danger",
              message: "Farmer id already exists. Try a different one.",
            });
          } else {
            setAlert({
              variant: "danger",
              message: "Edit failed. Please try again.",
            });
          }
        }
        setSubmitting(false);
      });
    } else {
      axios
        .post(`${process.env.REACT_APP_API_URL}/api/farmer/addFarmer`, {
          values: payload,
        })
        .then((res) => {
          if (res.data?.message === "Farmer added successfully") {
            onSuccess && onSuccess(res.data.farmer);
            onClose();
          } else if (res.data?.Message === "Mobile number already exists") {
            setAlert({
              variant: "danger",
              message: "A farmer with this mobile number already exists.",
            });
          } else if (res.data?.Message === "Farmer id already exists") {
            setAlert({
              variant: "danger",
              message: "A farmer with this registration number already exists.",
            });
          } else {
            setAlert({
              variant: "danger",
              message: "Something went wrong. Please try again.",
            });
          }
        })
        .catch(() =>
          setAlert({
            variant: "danger",
            message: "Network error. Please try again.",
          }),
        )
        .finally(() => setSubmitting(false));
    }
  };

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 1100,
          backgroundColor: "rgba(15,30,20,0.52)",
          backdropFilter: "blur(2px)",
        }}
      />

      {/* Panel */}
      <div
        style={{
          position: "fixed",
          top: 0,
          right: 0,
          bottom: 0,
          zIndex: 1200,
          width: "min(720px, 96vw)",
          backgroundColor: C.page,
          display: "flex",
          flexDirection: "column",
          boxShadow: "-6px 0 48px rgba(0,0,0,0.22)",
          animation: "drawerIn 0.22s cubic-bezier(.4,0,.2,1)",
        }}
      >
        <style>{`
          @keyframes drawerIn {
            from { transform: translateX(100%); opacity: 0.6; }
            to   { transform: translateX(0);    opacity: 1;   }
          }
        `}</style>

        {/* ── Header ── */}
        <div
          style={{
            backgroundColor: C.dark,
            padding: "0.85rem 1.25rem",
            display: "flex",
            alignItems: "center",
            gap: "0.85rem",
            flexShrink: 0,
            borderBottom: `1px solid rgba(255,255,255,0.08)`,
          }}
        >
          {/* Icon badge */}
          <div
            style={{
              width: 40,
              height: 40,
              borderRadius: "50%",
              flexShrink: 0,
              backgroundColor: isEdit ? C.mid : C.gold,
              color: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "0.95rem",
            }}
          >
            <FontAwesomeIcon icon={isEdit ? faPen : faUserPlus} />
          </div>

          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{ fontWeight: 700, fontSize: "0.97rem", color: "#fff" }}
            >
              {isEdit
                ? `Edit — ${farmer?.fullName || "Farmer"}`
                : "Add New Farmer"}
            </div>
            <div
              style={{
                fontSize: "0.7rem",
                color: "rgba(255,255,255,0.5)",
                marginTop: 2,
              }}
            >
              {isEdit
                ? "Update farmer profile information"
                : "Fill in the details to register a new farmer"}
            </div>
          </div>

          {isEdit && (
            <button
              type="button"
              onClick={() => setShowDeleteConfirm(true)}
              style={hdrBtn("rgba(220,53,69,0.25)", "#ff8a8a")}
              title="Delete Farmer"
            >
              <FontAwesomeIcon icon={faTrash} />
            </button>
          )}
          <button
            onClick={onClose}
            style={hdrBtn("rgba(255,255,255,0.1)", "#fff")}
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        {/* ── Body ── */}
        {loadingData ? (
          <div
            style={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: C.textGr,
              fontSize: "0.9rem",
            }}
          >
            Loading farmer data…
          </div>
        ) : (
          <Formik
            initialValues={initialValues}
            validationSchema={schema}
            enableReinitialize
            onSubmit={handleSubmit}
          >
            {({ values, errors, touched, isSubmitting, setFieldValue }) => (
              <Form
                style={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  overflow: "hidden",
                }}
              >
                {/* Scrollable area */}
                <div
                  style={{
                    flex: 1,
                    overflowY: "auto",
                    padding: "1rem 1.25rem",
                  }}
                >
                  {/* Personal Information */}
                  <Section title="Personal Information" icon={faUser}>
                    <FF label="Farmer ID">
                      <Field
                        name="regNo"
                        style={inputDisabled}
                        disabled
                        placeholder={
                          regNoLoading
                            ? "Generating..."
                            : isEdit
                            ? ""
                            : "Select state to generate ID"
                        }
                      />
                    </FF>
                    <FF
                      label="Full Name"
                      required
                      error={touched.fullName && errors.fullName}
                    >
                      <Field
                        name="fullName"
                        style={input}
                        placeholder="Farmer's full name"
                      />
                    </FF>
                    <FF
                      label="Mobile Number"
                      required
                      error={touched.mobileNumber && errors.mobileNumber}
                    >
                      <Field
                        name="mobileNumber"
                        style={isEdit ? inputDisabled : input}
                        disabled={isEdit}
                        placeholder="10-digit mobile"
                        maxLength={10}
                      />
                    </FF>
                    <FF
                      label="WhatsApp Number"
                      error={touched.whatsappNumber && errors.whatsappNumber}
                    >
                      <Field
                        name="whatsappNumber"
                        style={input}
                        placeholder="10-digit WhatsApp"
                        maxLength={10}
                      />
                    </FF>
                  </Section>

                  {/* Location */}
                  <Section title="Location" icon="📍">
                    <FF label="State" required>
                      <Select
                        placeholder="Select State"
                        value={selectedState}
                        onChange={async (opt) => {
                          setSelectedState(opt);
                          setSelectedDistrict(null);
                          setSelectedTaluka(null);
                          if (!isEdit && opt?.value) {
                            setRegNoLoading(true);
                            const res = await getNextRegNo(opt.value);
                            if (res?.data?.nextRegNo) {
                              setFieldValue("regNo", res.data.nextRegNo);
                            }
                            setRegNoLoading(false);
                          }
                        }}
                        options={stateOptions}
                        styles={selectStyles}
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                      />
                    </FF>
                    <FF label="District" required>
                      <Select
                        placeholder={
                          selectedState
                            ? "Select District"
                            : "Select State first"
                        }
                        value={selectedDistrict}
                        onChange={(opt) => {
                          setSelectedDistrict(opt);
                          setSelectedTaluka(null);
                        }}
                        isDisabled={!selectedState}
                        options={districtOptions}
                        styles={selectStyles}
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                      />
                    </FF>
                    <FF label="Taluka">
                      <Select
                        placeholder={
                          selectedDistrict
                            ? "Select Taluka"
                            : "Select District first"
                        }
                        value={selectedTaluka}
                        onChange={setSelectedTaluka}
                        isDisabled={!selectedDistrict}
                        options={talukaOptions}
                        styles={selectStyles}
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                        isClearable
                      />
                    </FF>
                    <FF label="Town / Village">
                      <Field
                        name="townVillage"
                        style={input}
                        placeholder="Village or town name"
                      />
                    </FF>
                    <FF
                      label="Pin Code"
                      error={touched.pinCode && errors.pinCode}
                    >
                      <Field
                        name="pinCode"
                        style={input}
                        placeholder="6-digit PIN code"
                        maxLength={6}
                      />
                    </FF>
                  </Section>

                  {/* Crop Information */}
                  <Section title="Crop Information" icon={faSeedling}>
                    <Full>
                      <FF label="Currently Cultivated Crops">
                        <Select
                          isMulti
                          placeholder="Select current crops…"
                          value={currentCrops.map((c) => ({
                            value: c,
                            label: c,
                          }))}
                          onChange={(opts) =>
                            setCurrentCrops((opts || []).map((o) => o.value))
                          }
                          options={allCrops.map((c) => ({
                            value: c,
                            label: c,
                          }))}
                          styles={selectStyles}
                          menuPortalTarget={document.body}
                          menuPosition="fixed"
                        />
                      </FF>
                    </Full>
                    <Full>
                      <FF label="Previously Cultivated Crops">
                        <Select
                          isMulti
                          placeholder="Select previous crops…"
                          value={previousCrops.map((c) => ({
                            value: c,
                            label: c,
                          }))}
                          onChange={(opts) =>
                            setPreviousCrops((opts || []).map((o) => o.value))
                          }
                          options={allCrops.map((c) => ({
                            value: c,
                            label: c,
                          }))}
                          styles={selectStyles}
                          menuPortalTarget={document.body}
                          menuPosition="fixed"
                        />
                      </FF>
                    </Full>
                  </Section>

                  {/* Farm & Irrigation */}
                  <Section title="Farm & Irrigation" icon={faTint}>
                    <FF
                      label="Land Acreage"
                      error={touched.landAcreage && errors.landAcreage}
                    >
                      <Field
                        name="landAcreage"
                        style={input}
                        placeholder="e.g. 5.5"
                      />
                    </FF>
                    <FF label="Irrigation Source">
                      <Field
                        name="irrigSource"
                        style={input}
                        placeholder="e.g. Borewell"
                      />
                    </FF>
                    <FF label="Irrigation Facilities">
                      <Field
                        name="irrigFacilities"
                        style={input}
                        placeholder="e.g. Drip"
                      />
                    </FF>
                    <FF label="Land Type">
                      <Field
                        name="landType"
                        style={input}
                        placeholder="e.g. Black soil"
                      />
                    </FF>
                  </Section>

                  {/* Soil Testing & Customer Status */}
                  <Section
                    title="Soil Testing & Customer Status"
                    icon={faFlask}
                  >
                    <FF label="Soil Testing Status">
                      <Field
                        as="select"
                        name="soilTestingStatus"
                        style={{ ...input, cursor: "pointer" }}
                      >
                        <option value="Not Done">Not Done</option>
                        <option value="In Progress">In Progress</option>
                        <option value="Done">Done</option>
                      </Field>
                    </FF>
                    <FF label="Soil Testing Laboratory Name">
                      <Field
                        name="soilTestingLabName"
                        style={input}
                        placeholder="e.g. Agri Lab Pune"
                      />
                    </FF>
                    <FF label="Last Soil Tested On">
                      <Field
                        name="lastSoilTestOn"
                        style={input}
                        placeholder="e.g. 15 Jan 2024"
                      />
                    </FF>
                    <FF label="Existing Customer">
                      <Field
                        as="select"
                        name="existingCustomer"
                        style={{ ...input, cursor: "pointer" }}
                      >
                        <option value="No">No</option>
                        <option value="Yes">Yes</option>
                      </Field>
                    </FF>
                    {values.existingCustomer === "Yes" && (
                      <FF label="Using Products Since (Year)">
                        <Field
                          name="usingProductsSince"
                          style={input}
                          placeholder="e.g. 2020"
                        />
                      </FF>
                    )}
                    <FF label="Call Type">
                      <Field
                        as="select"
                        name="phonecallio"
                        style={{ ...input, cursor: "pointer" }}
                      >
                        <option value="">Select type</option>
                        <option value="Incoming">Incoming</option>
                        <option value="Outgoing">Outgoing</option>
                      </Field>
                    </FF>
                    <FF label="Heard About">
                      <Field
                        as="select"
                        name="heardAbout"
                        style={{ ...input, cursor: "pointer" }}
                      >
                        <option value="">Select option</option>
                        <option value="Farmer">Farmer</option>
                        <option value="Promotional material">Promotional material</option>
                        <option value="Staff">Staff</option>
                        <option value="Exhibition">Exhibition</option>
                        <option value="Dealer">Dealer</option>
                        <option value="Other">Other</option>
                      </Field>
                    </FF>
                    <Full>
                      <FF label="Dealer Preference">
                        <Field
                          name="dealerPreference"
                          style={input}
                          placeholder="Enter preferred dealer name"
                        />
                      </FF>
                    </Full>
                  </Section>

                  {/* Products Used */}
                  <Section title="Products Used" icon={faBox}>
                    <Full>
                      <FF label="Products Used by Farmer">
                        <CreatableSelect
                          isMulti
                          placeholder="Select or type product names…"
                          value={selectedProducts.map((p) => ({
                            value: p,
                            label: p,
                          }))}
                          onChange={(opts) =>
                            setSelectedProducts(
                              (opts || []).map((o) => o.value),
                            )
                          }
                          options={allProducts.map((p) => ({
                            value: p,
                            label: p,
                          }))}
                          styles={selectStyles}
                          menuPortalTarget={document.body}
                          menuPosition="fixed"
                          formatCreateLabel={(input) => `Add "${input}"`}
                        />
                      </FF>
                    </Full>
                  </Section>
                </div>

                {/* ── Footer ── */}
                <div
                  style={{
                    padding: "0.85rem 1.25rem",
                    borderTop: `1px solid ${C.border}`,
                    backgroundColor: C.white,
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: 8,
                    flexShrink: 0,
                  }}
                >
                  <button
                    type="button"
                    onClick={onClose}
                    style={{
                      height: 38,
                      padding: "0 1.2rem",
                      borderRadius: 8,
                      border: `1.5px solid ${C.border}`,
                      backgroundColor: C.white,
                      color: C.textDk,
                      fontSize: "0.87rem",
                      fontWeight: 600,
                      cursor: "pointer",
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    style={{
                      height: 38,
                      padding: "0 1.6rem",
                      borderRadius: 8,
                      border: "none",
                      background: `linear-gradient(135deg, ${C.mid} 0%, #1e4530 100%)`,
                      color: "#fff",
                      fontSize: "0.87rem",
                      fontWeight: 700,
                      cursor: isSubmitting ? "not-allowed" : "pointer",
                      opacity: isSubmitting ? 0.7 : 1,
                      display: "flex",
                      alignItems: "center",
                      gap: 7,
                    }}
                  >
                    <FontAwesomeIcon
                      icon={faSave}
                      style={{ fontSize: "0.75rem" }}
                    />
                    {isSubmitting
                      ? "Saving…"
                      : isEdit
                        ? "Save Changes"
                        : "Add Farmer"}
                  </button>
                </div>
              </Form>
            )}
          </Formik>
        )}
      </div>

      {alert && (
        <ModalAlert
          size="lg"
          variant={alert.variant}
          message={alert.message}
          resetCallback={() => setAlert(null)}
        />
      )}

      {showDeleteConfirm && (
        <ConfirmBox
          heading="Delete Farmer"
          message={`Are you sure you want to delete ${farmer?.fullName || "this farmer"}? This action cannot be undone.`}
          confirmAction={async () => {
            const mobileNumber = farmer?.mobileNumber;
            const fullName = farmer?.fullName;
            const res = await deleteFarmerApi(mobileNumber);
            if (res?.data?.message === "Farmer deleted successfully") {
              onDeleteSuccess && onDeleteSuccess(fullName, mobileNumber);
              onClose();
              onSuccess && onSuccess();
            } else {
              setAlert({ variant: "danger", message: "Failed to delete farmer. Please try again." });
            }
          }}
          resetCallback={setShowDeleteConfirm}
        />
      )}
    </>
  );
};

export default FarmerFormDrawer;
